<script setup>
import { useFetchEnrolledHealthSeekers } from '@/hooks/use-health-seekers';
import { computed, onMounted, ref } from 'vue';

// Props
defineProps({
  data: {
    type: Array,
    default: () => []
  }
})

// Fetch data composable
const { data, isLoading, error, refetch } = useFetchEnrolledHealthSeekers()

// Selected rows state (stores indices)
const selectedRows = ref([])

// Search state
const searchQuery = ref('')

// Processing state
const isProcessing = ref(false)

// Pagination state
const currentPage = ref(1)
const itemsPerPage = ref(15)

// Filtered data based on search
const filteredData = computed(() => {
  if (!searchQuery.value.trim()) {
    return data.value
  }

  const query = searchQuery.value.toLowerCase()
  return data.value.filter(item => {
    return (
      item.scheme_code?.toLowerCase().includes(query) ||
      item.option_code?.toLowerCase().includes(query) ||
      item.membership_number?.toLowerCase().includes(query) ||
      item.dependant_code?.toLowerCase().includes(query)
    )
  })
})

// Calculate pagination
const totalPages = computed(() => {
  return Math.ceil(filteredData.value.length / itemsPerPage.value)
})

const startIndex = computed(() => {
  return (currentPage.value - 1) * itemsPerPage.value
})

const endIndex = computed(() => {
  return Math.min(currentPage.value * itemsPerPage.value, filteredData.value.length)
})

// Paginated data
const paginatedData = computed(() => {
  return filteredData.value.slice(startIndex.value, endIndex.value)
})

// Get original index from paginated data
const getOriginalIndex = (paginatedIndex) => {
  const item = paginatedData.value[paginatedIndex]
  return data.value.findIndex(
    d => d.membership_number === item.membership_number && d.dependant_code === item.dependant_code
  )
}

// Check if a row is selected
const isSelected = (index) => {
  return selectedRows.value.includes(index)
}

// Toggle single row selection
const toggleRow = (index) => {
  const position = selectedRows.value.indexOf(index)
  if (position === -1) {
    selectedRows.value.push(index)
  } else {
    selectedRows.value.splice(position, 1)
  }
}

// Check if all rows on current page are selected
const isAllSelected = computed(() => {
  return paginatedData.value.length > 0 && paginatedData.value.every((_, idx) => isSelected(getOriginalIndex(idx)))
})

// Check if some (but not all) rows on current page are selected
const isIndeterminate = computed(() => {
  const selectedCount = paginatedData.value.filter((_, idx) => isSelected(getOriginalIndex(idx))).length
  return selectedCount > 0 && selectedCount < paginatedData.value.length
})

// Toggle all rows on current page
const toggleAll = () => {
  if (isAllSelected.value) {
    paginatedData.value.forEach((_, idx) => {
      const originalIdx = getOriginalIndex(idx)
      const position = selectedRows.value.indexOf(originalIdx)
      if (position > -1) {
        selectedRows.value.splice(position, 1)
      }
    })
  } else {
    paginatedData.value.forEach((_, idx) => {
      const originalIdx = getOriginalIndex(idx)
      if (!selectedRows.value.includes(originalIdx)) {
        selectedRows.value.push(originalIdx)
      }
    })
  }
}

// Pagination functions
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

// Reset to first page when search changes
const resetPagination = () => {
  currentPage.value = 1
}

// Watch search and reset pagination
import { watch } from 'vue';
watch(searchQuery, () => {
  resetPagination()
})

// Clear selection
const clearSelection = () => {
  selectedRows.value = []
}

// Handle bulk action
const handleBulkAction = () => {
  const selectedData = selectedRows.value.map(index => data.value[index])
  console.log('Processing selected items:', selectedData)
  alert(`Processing ${selectedData.length} item(s)`)
}

// Handle process sunset sync
const handleProcessSunsetSync = async () => {
  const selectedData = selectedRows.value.map(index => data.value[index])
  
  isProcessing.value = true
  try {
    console.log('Processing sunset sync for:', selectedData)
    alert(`Sunset sync processing started for ${selectedData.length} item(s)`)
    clearSelection()
  } catch (err) {
    console.error('Error processing sunset sync:', err)
    alert('Error processing sunset sync. Please try again.')
  } finally {
    isProcessing.value = false
  }
}

// Format date helper
const formatDate = (dateString) => {
  return dateString.replace(/\//g, '-')
}

// Expose selected data to parent component
defineExpose({
  selectedRows,
  getSelectedData: () => selectedRows.value.map(index => data.value[index]),
  clearSelection
})

// Fetch data on mount
onMounted(() => {
  refetch()
})
</script>

<template>
  <div class="w-full space-y-4 p-8">
    <!-- Header with selection info and actions -->
    <div class="flex items-center justify-between">
      <div class="text-sm text-gray-600">
        <span v-if="selectedRows?.length > 0" class="font-medium">
          {{ selectedRows?.length }} of {{ filteredData?.length }} row(s) selected
        </span>
        <h2 v-else class="text-2xl text-slate-600">Eligible Health Seekers ({{ data?.length }})</h2>
      </div>
      <div class="flex gap-2">
        <button
          v-if="selectedRows?.length > 0"
          @click="clearSelection"
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Clear Selection
        </button>
        <button
          v-if="selectedRows?.length > 0"
          @click="handleProcessSunsetSync"
          :disabled="isProcessing"
          class="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span v-if="isProcessing" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          {{ isProcessing ? 'Processing...' : `Process Sunset Sync (${selectedRows?.length})` }}
        </button>
        <button
          v-if="selectedRows.length > 0"
          @click="handleBulkAction"
          class="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Process Selected ({{ selectedRows.length }})
        </button>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="flex gap-2">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search by scheme, option, membership number, or dependant code..."
        class="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button
        v-if="searchQuery"
        @click="searchQuery = ''"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
      >
        Clear
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading data...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <p class="text-red-800 font-medium">Error loading data</p>
      <p class="text-red-700 text-sm">{{ error }}</p>
      <button
        @click="refetch"
        class="mt-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
      >
        Retry
      </button>
    </div>

    <!-- Table -->
    <div v-else class="border rounded-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table v-if="data" class="w-full">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th class="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleAll"
                  class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Scheme
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Option
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Membership Number
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Dependant
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Auth Period
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Sunset
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Tariff Codes
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(item, index) in paginatedData"
              :key="`${item.membership_number}-${item.dependant_code}-${index}`"
              :class="[
                'hover:bg-gray-50 transition-colors',
                isSelected(getOriginalIndex(index)) ? 'bg-blue-50' : ''
              ]"
            >
              <td class="px-4 py-3">
                <input
                  type="checkbox"
                  :checked="isSelected(getOriginalIndex(index))"
                  @change="toggleRow(getOriginalIndex(index))"
                  class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-600"
                />
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ item.scheme_code }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span class="text-sm text-gray-900 font-medium">{{ item.option_code }}</span>
              </td>
              <td class="px-4 py-3">
                <span class="text-sm font-mono text-gray-700">{{ item.membership_number }}</span>
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                  {{ item.dependant_code }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm">
                  <div class="text-gray-900">{{ formatDate(item.auth_start_date) }}</div>
                  <div class="text-gray-500 text-xs">to {{ formatDate(item.auth_end_date) }}</div>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-1 text-sm text-gray-700">
                  <span>2025-12-31</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="code in item.tariff_codes"
                    :key="code"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                  >
                    {{ code }}
                  </span>
                </div>
              </td>
            </tr>
            <tr v-if="filteredData?.length === 0 && !isLoading">
              <td colspan="8" class="px-4 py-8 text-center text-gray-500">
                No results found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="filteredData?.length > 0" class="flex items-center justify-between">
      <div class="text-sm text-gray-600">
        Showing <span class="font-medium">{{ startIndex + 1 }}</span> to <span class="font-medium">{{ endIndex }}</span> of <span class="font-medium">{{ filteredData?.length }}</span> results
      </div>
      <div class="flex gap-2 items-center">
        <select
          v-model.number="itemsPerPage"
          class="py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option :value="10">10 per page</option>
          <option :value="25">25 per page</option>
          <option :value="50">50 per page</option>
          <option :value="100">100 per page</option>
        </select>
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          class="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Previous
        </button>
        <div class="flex items-center gap-1">
          <span class="text-sm text-gray-600">
            Page <span class="font-medium">{{ currentPage }}</span> of <span class="font-medium">{{ totalPages }}</span>
          </span>
        </div>
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          class="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped>
</style>